import { useEffect, useRef, useState } from "react";
import { useResponsiveView } from "@gui/hooks/useResponsiveView";
import type { PageFilterSelection, PageMenuItem, PageTopToolbarConfig, PageViewAction } from "./PageLayout.types";
import { clearFilters, countActiveFilters, hasAnySelection, toggleFilter } from "./pageFilters";

/**
 * The top toolbar's own state: which popup is open, on a phone the filter choices held in the
 * bottom sheet until Apply, and a debounced search's typed text. What the top toolbar shows is the page's config;
 * nothing here is page data.
 */
export function usePageTopToolbarViewModel(config: PageTopToolbarConfig) {
	const { isMobile } = useResponsiveView();
	const filters = config.filters;
	const search = config.search;
	const debounceMs = search?.debounceMs;

	// A debounced search's text lives here, so typing re-renders the toolbar and not the page under it.
	const [searchDraft, setSearchDraft] = useState(search?.value ?? "");
	// The last value the page holds, from us or from itself; a different one arriving is the page's own change.
	const pageSearchValue = useRef(search?.value ?? "");
	// The page's latest callback, so the wait ends by calling the current one, not the one from the render where the
	// text last changed (which may close over state the page has changed since).
	const onSearchChange = useRef(search?.onChange);
	onSearchChange.current = search?.onChange;
	useEffect(() => {
		const value = search?.value ?? "";
		if (value === pageSearchValue.current) return;
		pageSearchValue.current = value;
		setSearchDraft(value);
	}, [search?.value]);
	useEffect(() => {
		if (debounceMs === undefined || searchDraft === pageSearchValue.current) return;
		const timer = setTimeout(() => {
			pageSearchValue.current = searchDraft;
			onSearchChange.current?.(searchDraft);
		}, debounceMs);
		return () => clearTimeout(timer);
		// Only the typed text starts the wait; the page's callback is read from the ref when it fires.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchDraft]);

	const [overflowAnchor, setOverflowAnchor] = useState<HTMLElement | null>(null);
	const [filterAnchor, setFilterAnchor] = useState<HTMLElement | null>(null);
	const [sheetOpen, setSheetOpen] = useState(false);
	// The menu a menu action opened: its choices, and what it is anchored to.
	// The menu a menu action opened: whose it is, its choices (as they were when it opened), and its anchor.
	const [actionMenu, setActionMenu] = useState<{
		owner: string;
		items: readonly PageMenuItem[];
		anchor: HTMLElement;
	} | null>(null);
	// The sheet edits a copy so the list does not jump while the user is still choosing.
	const [draft, setDraft] = useState<PageFilterSelection>({});

	const groups = filters?.groups ?? [];

	// Crossing the breakpoint swaps the top toolbar's tree, and the popup that was open belongs to the
	// tree that left. The open flags below are gated on the layout, so it never shows for even one
	// frame on the other one; this then drops the stale state, so it does not reopen on the way back.
	useEffect(() => {
		setOverflowAnchor(null);
		setFilterAnchor(null);
		setSheetOpen(false);
		setActionMenu(null);
	}, [isMobile]);

	/** Desktop chips and inline chips apply on the spot. */
	const toggleApplied = (groupId: string, value: string) => {
		if (filters) filters.onChange(toggleFilter(filters.selected, groupId, value));
	};

	const clearApplied = () => {
		if (filters) filters.onChange(clearFilters(groups, filters.selected));
	};

	const openFilters = (anchor: HTMLElement) => {
		if (!filters) return;
		if (isMobile) {
			setDraft(filters.selected);
			setSheetOpen(true);
		} else {
			setFilterAnchor(anchor);
		}
	};

	const applyDraft = () => {
		filters?.onChange(draft);
		setSheetOpen(false);
	};

	/** Presses an action: runs it, or opens its menu or popover at `anchor`. */
	const activate = (action: PageViewAction, anchor: HTMLElement) => {
		if ("menu" in action) setActionMenu({ owner: ownerOf(action), items: action.menu, anchor });
		else if ("onOpen" in action) action.onOpen(anchor);
		else action.onClick();
	};

	/** Picks an action from the ⋯ menu and closes it. A menu or popover it opens takes the ⋯ button as its anchor. */
	const runOverflowAction = (action: PageViewAction) => {
		const anchor = overflowAnchor;
		setOverflowAnchor(null);
		if ("onClick" in action) action.onClick();
		// No anchor only during the menu's exit, where a second pick is dropped rather than opened against nothing.
		else if (anchor) activate(action, anchor);
	};

	/** Runs a choice from an action's menu and closes it. */
	const runActionMenuItem = (item: PageMenuItem) => {
		const anchor = actionMenu?.anchor;
		setActionMenu(null);
		if (anchor) item.onClick(anchor);
	};

	return {
		isMobile,
		searchValue: debounceMs === undefined ? (search?.value ?? "") : searchDraft,
		editSearch: (next: string) => (debounceMs === undefined ? search?.onChange(next) : setSearchDraft(next)),
		activeFilterCount: filters ? countActiveFilters(groups, filters.selected) : 0,
		canClearFilters: filters ? hasAnySelection(filters.selected) : false,
		toggleApplied,
		clearApplied,
		openFilters,
		filterAnchor,
		filterPopoverOpen: !isMobile && filterAnchor !== null,
		closeFilterPopover: () => setFilterAnchor(null),
		sheetOpen: isMobile && sheetOpen,
		closeSheet: () => setSheetOpen(false),
		draft,
		toggleDraft: (groupId: string, value: string) => setDraft((d) => toggleFilter(d, groupId, value)),
		clearDraft: () => setDraft((d) => clearFilters(groups, d)),
		applyDraft,
		overflowAnchor,
		overflowOpen: isMobile && overflowAnchor !== null,
		openOverflow: setOverflowAnchor,
		closeOverflow: () => setOverflowAnchor(null),
		runOverflowAction,
		activate,
		actionMenu,
		/** Whether this menu action's menu is the one open, for its button's `aria-expanded`. */
		isMenuOpen: (action: PageViewAction) => actionMenu?.owner === ownerOf(action),
		closeActionMenu: () => setActionMenu(null),
		runActionMenuItem,
	};
}

const ownerOf = (action: PageViewAction) => action.id ?? action.label;

export type PageTopToolbarViewModel = ReturnType<typeof usePageTopToolbarViewModel>;
